import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { BadgeTone } from "@/ui";
import type {
  ClientStatus,
  ClientType,
  ClientLevel,
  ContractModel,
  ServiceType,
  PaymentStatus,
  PaymentType,
  PaymentMethod,
  Platform,
  CardSource,
  TaskStatus,
  Priority,
  ExpenseCategory,
  TeamRole,
  NotifSeverity,
} from "@/types";

type LabelMap<T extends string> = Record<T, { label: string; tone: BadgeTone }>;

/**
 * Enum → Badge tone (static; theme-independent) and the i18n key group used to
 * resolve a translated, human label. Render with `useLabels()` so labels react
 * to the active language. Keep the key order = the desired display/enumeration
 * order (object insertion order drives `Object.keys`).
 */
const TONES = {
  clientStatus: { ACTIVE: "success", ONBOARDING: "info", PAUSED: "warning", CHURNED: "danger" },
  clientType: { SERVICE: "danger", PARTNERSHIP: "warning" },
  clientLevel: { BEGINNER: "success", INTERMEDIATE: "info", EXPERT: "violet" },
  contractModel: { MONTHLY_SUBSCRIPTION: "info", DELIVERY_ORDER: "warning", COMMISSION: "danger", DRAFT: "default" },
  serviceType: {
    MEDIA_BUYER: "violet",
    CREATIVE: "success",
    MARKETING_STRATEGIST: "info",
    AI_AGENT: "default",
    AUTOMATION: "warning",
    WEBSITE_SAAS: "info",
  },
  paymentStatus: { RECEIVED: "success", PENDING: "warning", OVERDUE: "danger", CANCELLED: "default" },
  paymentType: { FEE: "success", WALLET_TOPUP: "info" },
  paymentMethod: { CASH: "default", CCP: "default", BANK_TRANSFER: "default", CARD: "default", OTHER: "default" },
  platform: { META: "info", TIKTOK: "default", GOOGLE: "success", OTHER: "default" },
  cardSource: { AGENCY_CARD: "violet", CLIENT_CARD: "info" },
  taskStatus: { TODO: "default", IN_PROGRESS: "info", ON_HOLD: "warning", DONE: "success" },
  priority: { LOW: "danger", MEDIUM: "warning", HIGH: "danger" },
  expenseCategory: { TEAM: "info", TOOLS: "success", RENT: "warning", FREELANCE: "violet", OTHER: "default" },
  teamRole: {
    OWNER: "violet",
    MEDIA_BUYER: "info",
    CREATIVE: "success",
    ACCOUNT_MANAGER: "warning",
    MARKETING_STRATEGIST: "info",
    DEVELOPER: "default",
  },
  notifSeverity: { INFO: "info", ALERT: "warning", CRITICAL: "danger" },
} as const satisfies Record<string, Record<string, BadgeTone>>;

function buildMap<T extends string>(t: (k: string) => string, group: string, tones: Record<string, BadgeTone>): LabelMap<T> {
  const out = {} as LabelMap<T>;
  for (const key of Object.keys(tones)) {
    out[key as T] = { label: t(`labels.${group}.${key}`), tone: tones[key]! };
  }
  return out;
}

export interface EnumLabels {
  CLIENT_STATUS: LabelMap<ClientStatus>;
  CLIENT_TYPE: LabelMap<ClientType>;
  CLIENT_LEVEL: LabelMap<ClientLevel>;
  CONTRACT_MODEL: LabelMap<ContractModel>;
  SERVICE_TYPE: LabelMap<ServiceType>;
  PAYMENT_STATUS: LabelMap<PaymentStatus>;
  PAYMENT_TYPE: LabelMap<PaymentType>;
  PAYMENT_METHOD: LabelMap<PaymentMethod>;
  PLATFORM: LabelMap<Platform>;
  CARD_SOURCE: LabelMap<CardSource>;
  TASK_STATUS: LabelMap<TaskStatus>;
  PRIORITY: LabelMap<Priority>;
  EXPENSE_CATEGORY: LabelMap<ExpenseCategory>;
  TEAM_ROLE: LabelMap<TeamRole>;
  NOTIF_SEVERITY: LabelMap<NotifSeverity>;
}

/** Translated enum label maps that react to the active language. */
export function useLabels(): EnumLabels {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      CLIENT_STATUS: buildMap(t, "clientStatus", TONES.clientStatus),
      CLIENT_TYPE: buildMap(t, "clientType", TONES.clientType),
      CLIENT_LEVEL: buildMap(t, "clientLevel", TONES.clientLevel),
      CONTRACT_MODEL: buildMap(t, "contractModel", TONES.contractModel),
      SERVICE_TYPE: buildMap(t, "serviceType", TONES.serviceType),
      PAYMENT_STATUS: buildMap(t, "paymentStatus", TONES.paymentStatus),
      PAYMENT_TYPE: buildMap(t, "paymentType", TONES.paymentType),
      PAYMENT_METHOD: buildMap(t, "paymentMethod", TONES.paymentMethod),
      PLATFORM: buildMap(t, "platform", TONES.platform),
      CARD_SOURCE: buildMap(t, "cardSource", TONES.cardSource),
      TASK_STATUS: buildMap(t, "taskStatus", TONES.taskStatus),
      PRIORITY: buildMap(t, "priority", TONES.priority),
      EXPENSE_CATEGORY: buildMap(t, "expenseCategory", TONES.expenseCategory),
      TEAM_ROLE: buildMap(t, "teamRole", TONES.teamRole),
      NOTIF_SEVERITY: buildMap(t, "notifSeverity", TONES.notifSeverity),
    }),
    [t],
  );
}
